// SPDX-License-Identifier: AGPL-3.0-or-later

import {PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import Authentication from '@app/features/auth/state/Authentication';
import * as UserCommands from '@app/features/user/commands/UserCommands';
import {NameplateMedia} from '@app/features/user/components/NameplateMedia';
import styles from '@app/features/user/components/modals/tabs/my_profile_tab/NameplatePicker.module.css';
import {NameplatePickerModal} from '@app/features/user/components/modals/tabs/my_profile_tab/NameplatePickerModal';
import NameplateCatalog from '@app/features/user/state/NameplateCatalog';
import Users from '@app/features/user/state/Users';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {CaretRightIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useEffect} from 'react';

const NAMEPLATE_TITLE_DESCRIPTOR = msg({
	message: 'Nameplate',
	comment: 'Section title in profile editing for the member-list nameplate picker.',
});
const NAMEPLATE_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Shown behind your name in the member list. Requires {premiumProductName}.',
	comment:
		'Helper text under the nameplate picker in profile editing. Preserve {premiumProductName}; it is inserted by code.',
});
const NAMEPLATE_NONE_DESCRIPTOR = msg({
	message: 'None',
	comment: 'Shown when no nameplate is equipped.',
});
const CHANGE_NAMEPLATE_DESCRIPTOR = msg({
	message: 'Change nameplate',
	comment: 'Opens the nameplate picker modal when a nameplate is already equipped.',
});
const CHOOSE_NAMEPLATE_DESCRIPTOR = msg({
	message: 'Choose nameplate',
	comment: 'Opens the nameplate picker modal when no nameplate is equipped.',
});

interface NameplatePickerProps {
	value: string | null;
	disabled?: boolean;
	onChange: (nameplateId: string | null) => void;
}

export const NameplatePicker = observer(function NameplatePicker({value, disabled = false, onChange}: NameplatePickerProps) {
	const {i18n} = useLingui();
	useEffect(() => {
		void NameplateCatalog.load();
	}, []);
	const selected = NameplateCatalog.getById(value);
	const applyNameplate = (nameplateId: string | null) => {
		onChange(nameplateId);
		const currentUserId = Authentication.currentUserId ?? Users.currentUserId;
		if (!currentUserId) {
			return;
		}
		Users.setNameplateId(currentUserId, nameplateId);
		void UserCommands.update({nameplate_id: nameplateId}).catch(() => undefined);
	};
	const openPicker = () => {
		if (disabled) {
			return;
		}
		ModalCommands.push(
			ModalCommands.modal(() => (
				<NameplatePickerModal value={value} onChange={applyNameplate} />
			)),
		);
	};
	return (
		<div data-flx="user.my-profile-tab.nameplate-picker">
			<div className={styles.header}>
				<div className={styles.title}>{i18n._(NAMEPLATE_TITLE_DESCRIPTOR)}</div>
				<div className={styles.description}>
					{i18n._(NAMEPLATE_DESCRIPTION_DESCRIPTOR, {premiumProductName: PREMIUM_PRODUCT_NAME})}
				</div>
			</div>
			<button
				type="button"
				className={styles.trigger}
				disabled={disabled}
				onClick={openPicker}
				data-flx="user.my-profile-tab.nameplate-picker.trigger"
			>
				<div className={styles.triggerPreview}>
					{selected ? (
						<NameplateMedia
							src={selected.image}
							catalogId={selected.id}
							active={false}
							className={styles.triggerMedia}
							title={selected.name}
						/>
					) : null}
					<div className={styles.triggerFade} />
					<div className={styles.triggerCopy}>
						<div className={styles.triggerName}>{selected?.name ?? i18n._(NAMEPLATE_NONE_DESCRIPTOR)}</div>
						{selected?.description ? <div className={styles.triggerCaption}>{selected.description}</div> : null}
					</div>
				</div>
				<span className={styles.triggerAction}>
					{i18n._(selected ? CHANGE_NAMEPLATE_DESCRIPTOR : CHOOSE_NAMEPLATE_DESCRIPTOR)}
					<CaretRightIcon size={16} weight="bold" />
				</span>
			</button>
		</div>
	);
});
